import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import SeoHead from "../components/SeoHead";
import ReadingProgressBar from "../components/ReadingProgressBar";
import {
  FAQS,
  FAQ_CATEGORIES,
  findFaqBySlug,
  relatedFaqs,
  buildFaqJsonLd,
} from "../lib/faqData";

/**
 * Single-question FAQ detail page (/faq/:slug).
 *
 * - H1 = question
 * - Full answer body (paragraphs + bullets, exactly mirroring the homepage
 *   accordion content so JSON-LD and visible copy stay in lock-step)
 * - 3 related FAQ links from the same category
 * - "Check your routine free →" CTA → /check
 * - Per-page FAQPage JSON-LD (single question) + canonical/title meta
 *
 * If the slug doesn't match any FAQ, we redirect to /faq.
 */
function PerPageJsonLd({ faq }) {
  useEffect(() => {
    const id = `faq-jsonld-detail-${faq.slug}`;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(buildFaqJsonLd([faq]));
    return () => {
      const t = document.getElementById(id);
      if (t && t.parentNode) t.parentNode.removeChild(t);
    };
  }, [faq]);
  return null;
}

function AnswerBody({ lines }) {
  return (
    <div className="prose-faq space-y-4 text-[16px] text-ink-soft leading-relaxed">
      {(() => {
        const out = [];
        let bullets = [];
        const flush = (key) => {
          if (bullets.length) {
            out.push(
              <ul key={`b-${key}`} className="list-disc pl-6 space-y-1.5">
                {bullets.map((b, i) => (
                  <li key={i}>{b.replace(/^•\s*/, "")}</li>
                ))}
              </ul>,
            );
            bullets = [];
          }
        };
        lines.forEach((line, i) => {
          if (line.startsWith("•")) {
            bullets.push(line);
          } else {
            flush(i);
            out.push(<p key={`p-${i}`}>{line}</p>);
          }
        });
        flush("end");
        return out;
      })()}
    </div>
  );
}

export default function FaqDetail() {
  const { slug } = useParams();
  const faq = findFaqBySlug(slug);

  if (!faq) {
    return <Navigate to="/faq" replace />;
  }

  const cat = FAQ_CATEGORIES.find((c) => c.id === faq.category);
  const related = relatedFaqs(faq.slug, 3);

  return (
    <main
      className="bg-cream-50 min-h-[80vh]"
      data-testid="faq-detail-page"
    >
      <ReadingProgressBar />
      <SeoHead
        title={faq.metaTitle}
        description={faq.metaDescription}
        canonicalPath={`/faq/${faq.slug}`}
      />
      <PerPageJsonLd faq={faq} />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <Link
          to="/faq"
          data-testid="faq-detail-back"
          className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.22em] font-bold text-ink-mute hover:text-ink transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All FAQs
        </Link>

        {cat && (
          <p
            data-testid="faq-detail-category"
            className="mt-6 text-[10.5px] uppercase tracking-[0.28em] text-ink-mute font-semibold"
          >
            {cat.label}
          </p>
        )}
        <h1
          data-testid="faq-detail-question"
          className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.025em] text-ink mt-2.5 leading-[1.08]"
        >
          {faq.question}
        </h1>

        <article
          data-testid="faq-detail-answer"
          className="mt-6 sm:mt-8 rounded-3xl bg-white border border-surface-line p-6 sm:p-8 shadow-card"
        >
          <AnswerBody lines={faq.answer} />
        </article>

        <Link
          to="/check"
          data-testid="faq-detail-cta-check"
          className="mt-8 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.01] active:scale-[0.99]"
        >
          Check your routine free
          <ArrowRight className="w-4 h-4" />
        </Link>

        {related.length > 0 && (
          <section
            data-testid="faq-detail-related"
            className="mt-12 sm:mt-14"
          >
            <p className="text-[10.5px] uppercase tracking-[0.28em] text-ink-mute font-semibold">
              Related questions
            </p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/faq/${r.slug}`}
                    data-testid={`faq-detail-related-${r.slug}`}
                    className="block rounded-2xl bg-white border border-surface-line p-4 hover:border-emerald-200 hover:shadow-card transition-all"
                  >
                    <span className="block text-[14.5px] font-semibold text-ink leading-snug">
                      {r.question}
                    </span>
                    <span className="inline-flex items-center gap-1 mt-2 text-[12px] font-semibold text-emerald-800">
                      Read answer →
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <nav
          className="mt-10 flex flex-wrap items-center gap-3 text-[13px]"
          aria-label="FAQ navigation"
        >
          <Link
            to="/faq"
            data-testid="faq-detail-browse-all"
            className="inline-flex items-center justify-center h-10 px-4 rounded-full bg-cream-100 hover:bg-cream-200 text-ink font-bold border border-surface-line transition-colors"
          >
            Browse all FAQs ({FAQS.length})
          </Link>
          <Link
            to="/"
            data-testid="faq-detail-home"
            className="inline-flex items-center justify-center h-10 px-4 rounded-full text-ink-soft hover:text-ink transition-colors"
          >
            ← Back to BlendSafe
          </Link>
        </nav>
      </div>
    </main>
  );
}
